export const PRETEXT_OVERVIEW_HEADING = 'Developed Pretexts and Attack Scenarios';

export const CAMPAIGN_SEQUENCE_HEADING = 'Campaign Sequence and Timeline';



const PRETEXT_SECTION_HEADING_RE = /^(?:Pretext|Scenario|Attack\s+Scenario|النص\s+التمهيدي|السيناريو|المقدمة|الخدعة)\s+(\d+)\s*(?::\s*(.+))?$/i;



export function parsePretextSectionHeading(heading) {

  const match = (heading || '').trim().match(PRETEXT_SECTION_HEADING_RE);

  if (!match) return null;

  return { index: Number(match[1]), name: (match[2] || '').trim() };

}



export function buildReportSectionKey(section, index) {

  if (section?.pretext_id) return `pretext:${section.pretext_id}`;

  const parsed = parsePretextSectionHeading(section?.heading);

  if (parsed?.index != null) return `pretext-index:${parsed.index}`;

  const heading = (section?.heading || '').trim().toLowerCase();

  if (heading) return `heading:${heading}`;

  return `section-index:${index}`;

}



export function findPretextIndexInList(pretexts, { id, name } = {}) {

  if (!pretexts?.length) return null;

  if (id) {

    const byId = pretexts.findIndex((p) => p.id === id);

    if (byId >= 0) return byId + 1;

  }

  const nameLower = (name || '').trim().toLowerCase();

  if (nameLower) {

    const byName = pretexts.findIndex((p) => (p.name || '').trim().toLowerCase() === nameLower);

    if (byName >= 0) return byName + 1;

  }

  return null;

}



export function sectionMatchesPretext(section, { id, name, index } = {}) {

  if (!section) return false;

  const normalizedName = (name || '').trim().toLowerCase();

  if (id && section.pretext_id === id) return true;

  const parsed = parsePretextSectionHeading(section.heading);

  if (parsed?.index != null && index != null && parsed.index === index) return true;

  if (parsed?.name && normalizedName && parsed.name.toLowerCase() === normalizedName) {

    return true;

  }

  if (normalizedName && (section.heading || '').toLowerCase().includes(normalizedName)) {

    return true;

  }

  return false;

}



export function reportHasPretextSections(report) {

  return (report?.sections || []).some((section) => isPretextReportSection(section));

}



export function reportReferencesPretext(report, pretext) {

  if (!report?.sections?.length || !pretext) return false;

  const index = findPretextIndexInList(

    [{ id: pretext.id, name: pretext.name }],

    { id: pretext.id, name: pretext.name },

  );

  return report.sections.some((section) =>

    sectionMatchesPretext(section, {

      id: pretext.id,

      name: pretext.name,

      index: index || undefined,

    }),

  );

}



function stripOverviewBullet(bullets, pretext, pretextIndex) {

  if (!bullets?.length) return bullets;

  const nameLower = (pretext.name || '').trim().toLowerCase();

  const index = pretextIndex ?? null;

  const indexPatterns = index != null

    ? [

        new RegExp(`\\bpretext\\s*${index}\\b`, 'i'),

        new RegExp(`\\bscenario\\s*${index}\\b`, 'i'),

        new RegExp(`\\battack\\s+scenario\\s*${index}\\b`, 'i'),

      ]

    : [];

  return bullets.filter((bullet) => {

    const text = String(bullet || '');

    const lower = text.toLowerCase();

    if (nameLower && lower.includes(nameLower)) return false;

    return !indexPatterns.some((pattern) => pattern.test(text));

  });

}



function pruneOverviewSection(sections) {

  const hasDetail = sections.some((section) => parsePretextSectionHeading(section.heading));

  if (hasDetail) return sections;

  return sections.filter(

    (section) => (section.heading || '').trim() !== PRETEXT_OVERVIEW_HEADING,

  );

}



function stripCampaignBullet(bullets, pretext) {

  if (!bullets?.length || !pretext?.name) return bullets;

  const name = pretext.name;

  return bullets.filter((bullet) => !String(bullet).includes(name));

}



export function removePretextFromReportData(report, pretext, pretexts = []) {

  if (!report) return report;

  const pretextIndex = findPretextIndexInList(pretexts, pretext);

  const sections = (report.sections || [])

    .map((section) => {

      if ((section.heading || '').trim() === PRETEXT_OVERVIEW_HEADING) {

        const bullets = stripOverviewBullet(section.bullets, pretext, pretextIndex);

        if (!bullets?.length) return null;

        const count = bullets.length;

        return {

          ...section,

          bullets,

          body: `This consultation produced ${count} distinct operational pretext(s). Each item below includes the complete nine-section pretext structure developed during the session.`,

        };

      }

      if ((section.heading || '').trim() === CAMPAIGN_SEQUENCE_HEADING) {

        const bullets = stripCampaignBullet(section.bullets, pretext);

        return { ...section, bullets };

      }

      if (sectionMatchesPretext(section, {

        id: pretext.id,

        name: pretext.name,

        index: pretextIndex ?? undefined,

      })) {

        return null;

      }

      return section;

    })

    .filter(Boolean);



  return { ...report, sections: pruneOverviewSection(sections) };

}



export function applyReportExclusions(

  report,

  { excludedPretextIds = [], excludedSectionKeys = [] } = {},

  pretexts = [],

) {

  if (!report) return report;

  const idSet = new Set(excludedPretextIds);

  const keySet = new Set(excludedSectionKeys);

  if (!idSet.size && !keySet.size) return report;



  let result = { ...report, sections: [...(report.sections || [])] };

  idSet.forEach((pretextId) => {

    const pretext = pretexts.find((p) => p.id === pretextId)

      || { id: pretextId, name: '' };

    result = removePretextFromReportData(result, pretext, pretexts);

  });



  if (!keySet.size) return result;



  const filtered = (result.sections || []).filter((section, index) => {

    const key = buildReportSectionKey(section, index);

    if (keySet.has(key)) return false;

    if (section.pretext_id && idSet.has(section.pretext_id)) return false;

    return true;

  });



  return { ...result, sections: pruneOverviewSection(filtered) };

}



export function removeSectionAtIndexFromReport(report, sectionIndex) {

  if (!report?.sections) return report;

  const sections = report.sections.filter((_, index) => index !== sectionIndex);

  return { ...report, sections };

}



export function findPretextForSection(section, pretexts) {

  if (!section || !pretexts?.length) return null;

  if (section.pretext_id) {

    const byId = pretexts.find((p) => p.id === section.pretext_id);

    if (byId) return byId;

  }

  const parsed = parsePretextSectionHeading(section.heading);

  if (parsed?.index) {

    const byIndex = pretexts[parsed.index - 1];

    if (byIndex) return byIndex;

  }

  if (parsed?.name) {

    const nameLower = parsed.name.toLowerCase();

    return (

      pretexts.find((p) => (p.name || '').trim().toLowerCase() === nameLower) || null

    );

  }

  return null;

}



export function isPretextReportSection(section) {

  if (!section) return false;

  if (section.pretext_id) return true;

  if ((section.heading || '').trim() === PRETEXT_OVERVIEW_HEADING) return true;

  return Boolean(parsePretextSectionHeading(section.heading));

}


